import { useRef, useState, type ReactNode } from 'react';
import { Button, makeStyles, Spinner, Text, tokens } from '@coreai/fluentui-react';
import type { TriggerContentState, RequestContentSide, RequestContentRetryTarget } from '@/types/monitoring';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { JsonViewer } from '../ActionDetailPanel/JsonViewer';

interface RequestContentProps {
  content: TriggerContentState;
  label: 'Input' | 'Output';
  onRetry?: ((side: RequestContentSide) => void) | undefined;
  retryingSide?: RequestContentRetryTarget | undefined;
  children?: ReactNode;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  announcement: visuallyHiddenStyle,
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
  },
});

/** Transport status is never inferred from the user's JSON payload. */
export function RequestContent({ content, label, onRetry, retryingSide = null, children }: RequestContentProps) {
  const styles = useStyles();
  const resultRef = useRef<HTMLDivElement>(null);
  const [announceRetry, setAnnounceRetry] = useState(false);
  const side = label === 'Input' ? 'input' : 'output';
  const isRetrying = retryingSide === side || retryingSide === 'all';
  const isStatusOnly = content.status === 'secured' || content.status === 'unavailable';
  const message = content.status === 'secured'
    ? `${label} is secured and cannot be displayed.`
    : content.status === 'unavailable'
      ? `${label} content is unavailable for this run.`
      : content.status === 'error' && content.reason === 'tooLarge'
        ? `${label} content is too large to display.`
        : content.status === 'error' && content.reason === 'accessDenied'
          ? `You do not have access to this ${side} content.`
          : `Unable to load ${side} content.`;
  const announcement = content.status === 'loading' ? `Loading ${side} content...`
    : isStatusOnly ? message
      : isRetrying ? `Retrying ${side} content...`
        : content.status === 'available' && announceRetry ? `${label} content loaded.`
          : content.status === 'error' && announceRetry ? `${label} content could not be loaded.` : '';

  return (
    <div
      ref={resultRef}
      className={styles.root}
      role="region"
      aria-label={`${label} content`}
      tabIndex={-1}
      data-testid={`request-${side}-content`}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setAnnounceRetry(false);
      }}
    >
      <div role="status" aria-live="polite" aria-atomic="true" className={isStatusOnly ? styles.state : styles.announcement}>
        <Text>{announcement}</Text>
      </div>
      {content.status === 'available' ? children ?? (
        <JsonViewer data={content.data} parseJsonStrings={false} label={label} downloadName={side} maxHeight="100%" contentTabIndex={0} />
      ) : isStatusOnly ? null : (
        <div className={styles.state}>
          {content.status === 'loading' ? <Spinner size="small" label={`Loading ${side}...`} /> : (
            <Text role={content.status === 'error' ? 'alert' : undefined}>{message}</Text>
          )}
          {content.status === 'error' && content.retryable && onRetry && (
            <Button
              onClick={event => {
                // Move focus before disabling/removing the initiating button, never after async completion.
                if (document.activeElement === event.currentTarget) {
                  resultRef.current?.focus();
                  setAnnounceRetry(true);
                }
                onRetry(side);
              }}
              disabled={retryingSide !== null}
            >Retry {side}</Button>
          )}
          {isRetrying && content.status === 'error' && <Spinner size="small" label={`Retrying ${side}...`} />}
        </div>
      )}
    </div>
  );
}
